import { Link, useNavigate } from 'react-router-dom';
import { Button, Flex, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SIC } from 'constants/projects';
import { projectsRoutes } from 'constants/routes';

import { usePageTitle } from 'util/hooks/usePageTitle';
import { projectsQueryKeys } from 'util/queryKeys';
import showToast from 'actions/toastActions';
import { createSamplingPlan } from 'store/projects/requests';
import { CreateSamplingPlanPayloadV2Type, ProjectType, RegistryConfig } from 'store/projects/types';
import { Header } from 'common';
import FullHeightLayoutFooter from 'common/FullHeightLayoutFooter';
import FullHeightLayoutWrap from 'common/FullHeightLayoutWrap';

import { useProjectRouteParams } from '../hooks';

import CreateConfirmationModal from './Form/CreateConfirmationModal';
import DepthQuantifyDetails from './Form/DepthQuantifyDetails';
import ExclusionLayerDetails from './Form/ExclusionLayerDetails';
import LandDetails from './Form/LandDetails';
import MeterCoreSampleDetails from './Form/MeterCoreSampleDetails';
import PhotoDetails from './Form/PhotoDetails';
import RegistryDetails from './Form/RegistryDetails';
import SoilHealthDetails from './Form/SoilHealthDetails';
import StratificationDetails from './Form/StratificationDetails';
import ThirtyCmSampleDetails from './Form/ThirtyCmSampleDetails';
import {
  getMeterCoreFormValues,
  getSoilHealthFormValues,
  getThirtyCmCoreFormValues,
  getWetAggregateStabilityFormValues,
} from './Form/utils';
import WetAggregateStabilityDetails from './Form/WetAggregateStabilityDetails';
import CreateSamplePlanPanel from './CreateSamplePlanPanel';
import { useProjectsTranslations } from './hooks';
import {
  MeterCoreFormProvider,
  SoilHealthFormProvider,
  ThirtyCmCoreFormProvider,
  useMeterCoreForm,
  useSamplePlanFormContext,
  useSoilHealthForm,
  useThirtyCmCoreForm,
  useWetAggregateStabilityForm,
  WetAggregateStabilityFormProvider,
} from './samplePlanFormContextV2';

type Props = {
  samplePlanConfig: RegistryConfig;
  project: ProjectType;
};

const CreateSamplePlanForm = ({ samplePlanConfig, project }: Props) => {
  const { projectId } = useProjectRouteParams();
  const translations = useProjectsTranslations();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const pageTitle = translations.createSamplingPlan;

  usePageTitle(pageTitle);

  const samplePlanForm = useSamplePlanFormContext();
  const queryClient = useQueryClient();

  const meterCoreForm = useMeterCoreForm({
    mode: 'uncontrolled',
    initialValues: getMeterCoreFormValues(samplePlanConfig?.meter_core),
  });

  const thirtyCmCoreForm = useThirtyCmCoreForm({
    mode: 'uncontrolled',
    initialValues: getThirtyCmCoreFormValues(samplePlanConfig?.thirty_cm_core),
  });

  const soilHealthForm = useSoilHealthForm({
    mode: 'uncontrolled',
    initialValues: getSoilHealthFormValues(samplePlanConfig?.soil_health),
  });

  const wetAggregateStabilityForm = useWetAggregateStabilityForm({
    mode: 'uncontrolled',
    initialValues: getWetAggregateStabilityFormValues(samplePlanConfig?.wet_aggregate_stability),
  });

  const createPlanMutation = useMutation({
    mutationFn: (planPayload: CreateSamplingPlanPayloadV2Type) => {
      return createSamplingPlan(planPayload);
    },
    onSuccess: () => {
      showToast(translations.success, 'success');
      queryClient.invalidateQueries({
        queryKey: projectsQueryKeys.projectSamplingPlans(projectId),
      });
      navigate(projectsRoutes.routes.project(projectId));
    },
  });

  const handleSubmit = () => {
    const samplePlanFormValues = samplePlanForm.getValues();
    const meterCoreFormValues = meterCoreForm.getValues();
    const thirtyCmCoreFormValues = thirtyCmCoreForm.getValues();
    const soilHealthFormValues = soilHealthForm.getValues();
    const wetAggregateStabilityFormValues = wetAggregateStabilityForm.getValues();

    const planPayload: CreateSamplingPlanPayloadV2Type = {
      field_ids: project?.fields.map((field) => field.id) || [],
      sample_plan: {
        project_id: projectId,
        project_type: samplePlanFormValues.project_type,
        method: samplePlanFormValues.method,
        user_defined_points: [], // TODO: define this type and implement logic
        user_defined_exclusions: [], // TODO: define this type and implement logic
        registry_list: samplePlanFormValues.registry_list,
        exclusion_layers: samplePlanFormValues.exclusion_layers,
        exclusion_thresholds: {
          tree_shrub: samplePlanFormValues.tree_shrub,
          slope: samplePlanFormValues.slope,
        },
        mc_dict: samplePlanFormValues.isMeterCore
          ? {
              ...meterCoreFormValues,
              lab: meterCoreFormValues.lab,
              splits: JSON.parse(meterCoreFormValues.splits),
              sic: meterCoreFormValues.sic === SIC, // Either SIC is selected or its the defualt -- boolean in Demeter
              density: Number(meterCoreFormValues.density),
              texture_density: meterCoreFormValues.add_texture_ph
                ? Number(meterCoreFormValues.texture_density)
                : null,
            }
          : null,
        step_probe_dict: samplePlanFormValues.isThirtyCmCore
          ? {
              ...thirtyCmCoreFormValues,
              lab: thirtyCmCoreFormValues.lab,
              sic: thirtyCmCoreFormValues.sic === SIC, // Either SIC is selected or its the defualt -- boolean in Demeter
              density: Number(thirtyCmCoreFormValues.density),
              texture_density: thirtyCmCoreFormValues.add_texture_ph
                ? Number(thirtyCmCoreFormValues.texture_density)
                : null,
            }
          : null,
        sh_dict: samplePlanFormValues.isSoilHealth
          ? {
              lab: soilHealthFormValues.lab,
              density: Number(soilHealthFormValues.density),
            }
          : null,
        was_dict: samplePlanFormValues.isWetAggregateStability
          ? {
              lab: wetAggregateStabilityFormValues.lab,
              density: Number(wetAggregateStabilityFormValues.density),
            }
          : null,
      },
    };

    createPlanMutation.mutate(planPayload, { onSettled: () => close() });
  };

  return (
    <MeterCoreFormProvider form={meterCoreForm}>
      <ThirtyCmCoreFormProvider form={thirtyCmCoreForm}>
        <SoilHealthFormProvider form={soilHealthForm}>
          <WetAggregateStabilityFormProvider form={wetAggregateStabilityForm}>
            <FullHeightLayoutWrap>
              <Header
                title={pageTitle}
                breadcrumbsItems={[
                  {
                    title: translations.allProjects,
                    to: projectsRoutes.routes.base,
                  },
                  {
                    title: `${translations.project}${project?.name ? `: ${project.name}` : ''}`,
                    to: projectsRoutes.routes.project(projectId),
                  },
                  {
                    title: translations.createSamplingPlan,
                  },
                ]}
              />
              <Flex h="100%" w="100%" style={{ overflowY: 'auto' }}>
                <CreateSamplePlanPanel />
                <Stack pb="xl" px="xl" flex={1} h="100%" style={{ overflowY: 'auto' }} mx="auto">
                  <RegistryDetails />
                  <LandDetails />
                  <DepthQuantifyDetails />
                  <MeterCoreSampleDetails />
                  <ThirtyCmSampleDetails />
                  <SoilHealthDetails />
                  <WetAggregateStabilityDetails />
                  <PhotoDetails />
                  <ExclusionLayerDetails />
                  <StratificationDetails />
                </Stack>
              </Flex>
              <FullHeightLayoutFooter>
                <Group ml="auto">
                  <Button variant="outline" component={Link} to={projectsRoutes.routes.base}>
                    {translations.cancel}
                  </Button>
                  <Button onClick={open}>{translations.generateSamplingPlan}</Button>
                </Group>
              </FullHeightLayoutFooter>
            </FullHeightLayoutWrap>
            <CreateConfirmationModal
              onClose={close}
              opened={opened}
              handleSubmit={handleSubmit}
              isPending={createPlanMutation.isPending}
            />
          </WetAggregateStabilityFormProvider>
        </SoilHealthFormProvider>
      </ThirtyCmCoreFormProvider>
    </MeterCoreFormProvider>
  );
};

export default CreateSamplePlanForm;
